/**
 * The store's events that happen in the browser: product lists, the cart,
 * the checkout's steps. See lib/analytics-store.php for the whole funnel and
 * for window.diakonStoreAnalytics, set before this runs:
 *
 *   collectUrl  where the cart's events wait (diakon_analytics_events)
 *   cookie      set while some do
 *   checkout    on the checkout page, the cart it sends
 */

// Two kinds of product list: Product Collection blocks, whose items come as
// one JSON for the block, and WooCommerce's classic loops (related products,
// up-sells, cross-sells), whose items come one JSON per <li>.
const BLOCK_LIST = '[data-block-name="woocommerce/product-collection"]';
const BLOCK_CARD = '[data-wp-key^="product-item-"]';
const CLASSIC_LIST = 'ul.products';
const CLASSIC_CARD = 'li.product';
const LIST = `${BLOCK_LIST}, ${CLASSIC_LIST}`;
const CARD = `${BLOCK_LIST} ${BLOCK_CARD}, ${CLASSIC_LIST} > ${CLASSIC_CARD}`;

// ── Product lists ───────────────────────────────────────────────────────────

/**
 * The heading nearest above an element, for a list the server could not
 * name: on a page, the section's own title is what the list is.
 *
 * @param {Element} element The list.
 * @return {string} Its text, or the page's title.
 */
function headingAbove(element) {
	let found = null;

	// In document order, so the last heading before the list is the one. The
	// product titles inside it come after it, and are not counted.
	for (const node of document.querySelectorAll(`h1, h2, h3, ${LIST}`)) {
		if (node === element) {
			break;
		}

		if (node.matches('h1, h2, h3')) {
			found = node;
		}
	}

	return (found ? found.textContent : document.title).trim();
}

/**
 * Parses a script of JSON, or null.
 *
 * @param {Element|null} script The <script type="application/json">.
 * @return {Object|null} Its data.
 */
function json(script) {
	try {
		return script ? JSON.parse(script.textContent) : null;
	} catch {
		return null;
	}
}

/**
 * A list's items, in the order shown, with their place in it.
 *
 * @param {Element} list A Product Collection block or a classic loop.
 * @return {{id: string, name: string, items: Object[], byCard: Map}|null} The
 *         list, and its items by their card.
 */
function readList(list) {
	let id;
	let name;
	let cards;

	if (list.matches(BLOCK_LIST)) {
		const data = json(list.querySelector('script.diakon-analytics-list'));

		if (!data) {
			return null;
		}

		id = data.id;
		name = data.name;
		cards = [...list.querySelectorAll(BLOCK_CARD)].map((card) => [
			card,
			data.items[card.dataset.wpKey.replace('product-item-', '')],
		]);
	} else {
		// Named after what WooCommerce draws it as, and titled by the heading
		// in its section: "Tutustu myös".
		const section = list.closest('.related, .up-sells, .cross-sells');
		const kind = section
			? ['related', 'up-sells', 'cross-sells'].find((c) =>
					section.classList.contains(c)
				)
			: 'products';

		id = `${kind}-0`;
		name = section?.querySelector('h2')?.textContent.trim() || '';
		cards = [...list.querySelectorAll(`:scope > ${CLASSIC_CARD}`)].map(
			(card) => [
				card,
				json(card.querySelector('script.diakon-analytics-item')),
			]
		);
	}

	name = name || headingAbove(list);

	const items = [];
	const byCard = new Map();

	cards.forEach(([card, item]) => {
		if (item) {
			const placed = {
				...item,
				index: items.length,
				item_list_id: id,
				item_list_name: name,
			};

			byCard.set(card, placed);
			items.push(placed);
		}
	});

	return { id, name, items, byCard };
}

/**
 * view_item_list for each list on the page, and select_item for a product
 * opened from one.
 *
 * @param {Function} push window.diakonPush.
 */
function initProductLists(push) {
	document.querySelectorAll(LIST).forEach((element) => {
		const list = readList(element);

		if (list && list.items.length) {
			push({
				event: 'view_item_list',
				ecommerce: {
					item_list_id: list.id,
					item_list_name: list.name,
					items: list.items,
				},
			});
		}
	});

	// Any link in the product's card, image or title.
	document.addEventListener(
		'click',
		(event) => {
			const card = event.target.closest(CARD);

			if (!card || !event.target.closest('a[href]')) {
				return;
			}

			const list = readList(card.closest(LIST));
			const item = list ? list.byCard.get(card) : null;

			if (item) {
				push({
					event: 'select_item',
					ecommerce: {
						item_list_id: list.id,
						item_list_name: list.name,
						items: [item],
					},
				});
			}
		},
		true
	);
}

// ── The cart ────────────────────────────────────────────────────────────────

/**
 * add_to_cart and remove_from_cart, collected from the server after
 * WooCommerce says the cart has changed.
 *
 * @param {Function} push   window.diakonPush.
 * @param {Object}   config window.diakonStoreAnalytics.
 */
function initCartEvents(push, config) {
	let collecting = null;

	// Waits a moment first: one change can be announced by several of the
	// events below, and the request that made it has to have saved them.
	const collect = () => {
		window.clearTimeout(collecting);
		collecting = window.setTimeout(async () => {
			try {
				const response = await window.fetch(config.collectUrl, {
					method: 'POST',
					credentials: 'same-origin',
				});
				const body = await response.json();

				(body.events || []).forEach(push);
			} catch {
				// Nothing to report then; the events wait for the next page.
			}
		}, 300);
	};

	// The product buttons and the mini-cart (WooCommerce's interactive
	// blocks), and the classic cart and checkout pages (jQuery).
	window.addEventListener('wc-blocks_store_sync_required', collect);
	document.body.addEventListener('wc-blocks_added_to_cart', collect);
	document.body.addEventListener('wc-blocks_removed_from_cart', collect);

	if (window.jQuery) {
		window
			.jQuery(document.body)
			.on('added_to_cart removed_from_cart updated_cart_totals', collect);
	}

	// A change made by a form that loaded a new page -- a product page's add
	// to cart, the cart's update button -- left the cookie.
	if (
		document.cookie
			.split('; ')
			.some((cookie) => cookie === `${config.cookie}=1`)
	) {
		collect();
	}
}

// ── The checkout ────────────────────────────────────────────────────────────

/**
 * The text of a choice's label: what the customer picked, as they saw it.
 *
 * @param {HTMLFormElement}  form  The checkout.
 * @param {HTMLInputElement} input The chosen input.
 * @return {string} The label, or the input's value without one.
 */
function labelOf(form, input) {
	const label = input.id
		? form.querySelector(`label[for="${window.CSS.escape(input.id)}"]`)
		: null;

	return (label ? label.textContent : input.value)
		.replace(/\s+/g, ' ')
		.trim();
}

/**
 * add_shipping_info and add_payment_info, as the checkout is sent.
 *
 * @param {Function} push     window.diakonPush.
 * @param {Object}   checkout The cart the checkout sends.
 */
function initCheckoutSteps(push, checkout) {
	const sent = {};

	// Once per choice: a checkout turned back by a missing field is sent
	// again, and the same choice is the same step.
	const step = (name, key, value) => {
		if (!value || sent[name] === value) {
			return;
		}

		sent[name] = value;
		push({ event: name, ecommerce: { ...checkout, [key]: value } });
	};

	// The classic checkout sends itself over AJAX from its submit event, so
	// that event is the moment the shipping and payment are given. Captured,
	// to come before WooCommerce stops it.
	document.addEventListener(
		'submit',
		(event) => {
			const form = event.target;

			if (!form.matches || !form.matches('form.checkout')) {
				return;
			}

			const shipping = form.querySelector(
				'input[name^="shipping_method"]:checked, input[type="hidden"][name^="shipping_method"]'
			);
			const shippingSelect = form.querySelector(
				'select[name^="shipping_method"]'
			);
			const payment = form.querySelector(
				'input[name="payment_method"]:checked, input[type="hidden"][name="payment_method"]'
			);

			if (shipping) {
				step(
					'add_shipping_info',
					'shipping_tier',
					labelOf(form, shipping)
				);
			} else if (shippingSelect) {
				step(
					'add_shipping_info',
					'shipping_tier',
					shippingSelect.selectedOptions[0]?.textContent.trim()
				);
			}

			if (payment) {
				step(
					'add_payment_info',
					'payment_type',
					labelOf(form, payment)
				);
			}
		},
		true
	);
}

/**
 * Everything above, where the store's config says there is a store.
 *
 * @param {Function} push window.diakonPush.
 */
export function initStoreAnalytics(push) {
	const config = window.diakonStoreAnalytics;

	if (!config) {
		return;
	}

	initProductLists(push);
	initCartEvents(push, config);

	if (config.checkout) {
		initCheckoutSteps(push, config.checkout);
	}
}
